import { useCallback, useRef, useState } from 'react';
import request from '../../utils/request';

const initialState = {
  status: 'idle',
  data: null,
  error: null,
};

const parseErrorMessage = (err) => {
  if (typeof err === 'string') return err;
  if (!err) return 'Something went wrong';
  if (err?.response?.data) {
    const data = err.response.data;
    if (typeof data === 'string') return data;
    return data.message || data.error || 'Unable to load lockbox info';
  }
  return err.message || 'Unable to load lockbox info';
};

export default function useLockboxDetails() {
  const [state, setState] = useState(initialState);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(initialState);
  }, []);

  const fetchDetails = useCallback(async (orderId) => {
    if (!orderId) {
      const message = 'Order id is required to load lockbox details';
      setState({ status: 'error', data: null, error: message });
      return Promise.reject(new Error(message));
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    try {
      const response = await request.get(`/properties/lockbox/order/${orderId}`, {
        signal: controller.signal,
      });
      const payload = response?.data || null;
      setState({ status: 'success', data: payload, error: null });
      abortRef.current = null;
      return payload;
    } catch (err) {
      if (controller.signal.aborted) {
        return Promise.reject(err);
      }
      const errorMessage = parseErrorMessage(err);
      setState({ status: 'error', data: null, error: errorMessage });
      abortRef.current = null;
      return Promise.reject(err);
    }
  }, []);

  return {
    ...state,
    fetchDetails,
    reset,
    isIdle: state.status === 'idle',
    isLoading: state.status === 'loading',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',
  };
}
